import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck } from "lucide-react";
import { TID } from "../../lib/testIds";

const QUICK_TOPICS = [
  { label: "Loan Payment", to: "/calculators/loan-payment" },
  { label: "Compound Interest", to: "/calculators/compound-interest" },
  { label: "Net Worth", to: "/calculators/net-worth" },
  { label: "Rent vs Buy", to: "/articles/rent-vs-buy-the-real-math" },
];

export function Hero() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    const term = q.trim().toLowerCase();
    if (!term) return navigate("/calculators");
    // Naive routing — match top-level keywords to relevant landing pages.
    const map = [
      ["loan", "/calculators/loan-payment"],
      ["mortgage", "/real-estate"],
      ["rent", "/articles/rent-vs-buy-the-real-math"],
      ["invest", "/invest"],
      ["retire", "/calculators/retirement"],
      ["business", "/business"],
      ["car", "/vehicles"],
      ["vehicle", "/vehicles"],
      ["debt", "/calculators/debt-snowball"],
      ["compound", "/calculators/compound-interest"],
      ["net worth", "/calculators/net-worth"],
    ];
    const hit = map.find(([k]) => term.includes(k));
    navigate(hit ? hit[1] : `/articles`);
  };

  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-white">
      <div className="bg-grid absolute inset-0 opacity-60 pointer-events-none" />
      <div className="bg-radial-fade absolute inset-0 pointer-events-none" />
      <div className="mml-container relative py-20 sm:py-24 lg:py-28">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
              <ShieldCheck className="h-3.5 w-3.5" />
              Plain English. No spam. No sales pitches.
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl text-balance">
              Make smarter money decisions —{" "}
              <span className="text-emerald-700">one number at a time.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
              MyMoneyLocal gives you free calculators and clear-headed guides for
              borrowing, investing, business, real estate, and vehicles. Nothing
              to install. Nothing to sign up for.
            </p>

            <form
              onSubmit={onSearch}
              role="search"
              className="mt-8 flex max-w-xl items-center gap-2 rounded-2xl border border-slate-300 bg-white p-2 shadow-sm transition-all focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/30"
            >
              <Search className="ml-2 h-5 w-5 text-slate-400" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Try “loan payment”, “compound interest”, “rent vs buy”…"
                aria-label="Search calculators and articles"
                data-testid={TID.heroSearchInput}
                className="flex-1 bg-transparent px-2 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              />
              <button
                type="submit"
                data-testid={TID.heroSearchSubmit}
                className="mml-btn-primary px-4 py-2 text-sm"
              >
                Search
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">
                Popular:
              </span>
              {QUICK_TOPICS.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                >
                  {t.label}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/calculators"
                data-testid={TID.heroPrimaryCta}
                className="mml-btn-primary"
              >
                Browse calculators
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/articles"
                data-testid={TID.heroSecondaryCta}
                className="mml-btn-secondary"
              >
                Read the guides
              </Link>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <HeroStatCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroStatCard() {
  return (
    <div className="relative animate-fade-up">
      <div className="absolute -inset-8 -z-10 rounded-[3rem] bg-emerald-100/50 blur-2xl" />
      <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl">
        <div className="flex items-center justify-between">
          <span className="mml-overline">Loan Payment</span>
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
            Live demo
          </span>
        </div>
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider text-slate-500">
            Monthly payment
          </p>
          <p className="mt-1 font-mono text-5xl font-bold text-emerald-700">
            $1,432
          </p>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-5">
          <div>
            <p className="text-xs text-slate-500">Loan amount</p>
            <p className="mt-1 font-mono text-lg font-semibold text-slate-900">
              $300,000
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Rate</p>
            <p className="mt-1 font-mono text-lg font-semibold text-slate-900">
              6.50%
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Term</p>
            <p className="mt-1 font-mono text-lg font-semibold text-slate-900">
              30 yrs
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Total interest</p>
            <p className="mt-1 font-mono text-lg font-semibold text-slate-900">
              $215k
            </p>
          </div>
        </div>
        <Link
          to="/calculators/loan-payment"
          className="mt-6 flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
        >
          Open the calculator
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

// end of file
